import type { OutputData } from "@editorjs/editorjs";
import {
  BuildingOffice2Icon,
  ChatBubbleOvalLeftEllipsisIcon,
  FolderOpenIcon,
  LockClosedIcon,
  MapIcon,
  QuestionMarkCircleIcon,
  ReceiptRefundIcon,
  ScaleIcon,
  UserIcon,
} from "@heroicons/react/24/solid";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Can } from "@/components/can";
import { TextEditor } from "@/components/text-editor";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TravelerPayments } from "@/features/payments/components/traveler-payments";
import { useCurrentUser } from "@/hooks/use-current-user";
import { copyToClipboard } from "@/lib/clipboard";

import type { ClaimWithRelations } from "../api/use-claim";
import { useClaimFeatureFlags } from "../api/use-claim";
import { useSaveAdminNote, useSaveCustomerNote } from "../api/use-claim-mutations";
import { isClaimWon } from "../lib/legal-steps";
import { isLeisureOffice } from "../lib/leisure-office";
import { isBoxFolderAvailable } from "../lib/open-box";
import { SetBusinessTravelCompany } from "./agent-feedback/set-business-travel-company";
import { ClaimReasonCxDisclosure } from "./circumstances/claim-reason-cx-disclosure";
import { ClaimDocuments, ProcedureDocuments } from "./claim-documents";
import { ClaimRequests } from "./claim-requests";
import { ExpensesList } from "./expenses-list";
import { MonetaryDemandsList } from "./monetary-demands-list";
import { SetClaimAdversary } from "./set-claim-adversary";
import { SetTravelAgencyAgent } from "./set-travel-agency-agent";

/**
 * adminNotes is a generic Json column — legacy claims can hold a plain string or string[]
 * from before the EditorJS migration. Only pass through well-formed EditorJS output, since
 * EditorJS throws when `data.blocks` isn't an array.
 */
function normalizeAdminNotes(adminNotes: ClaimWithRelations["adminNotes"]): OutputData | undefined {
  if (
    !adminNotes ||
    typeof adminNotes !== "object" ||
    Array.isArray(adminNotes) ||
    !("blocks" in adminNotes)
  ) {
    return undefined;
  }
  return adminNotes as OutputData;
}

/** Port of ClaimDetails.vue — « Résumé du dossier ». */
export function ClaimDetails({ claim }: { claim: ClaimWithRelations }) {
  const { can } = useCurrentUser();
  const saveCustomerNote = useSaveCustomerNote(claim.id);
  const saveAdminNote = useSaveAdminNote(claim.id);
  const { data: featureFlags } = useClaimFeatureFlags(claim.id);

  const showTravelerPayments =
    (featureFlags ?? []).includes("TRAVELER_PAYMENT") &&
    can(UPCLAIM_ROLES) &&
    isClaimWon(claim.claimLegalStep.name);

  const [customerNotes, setCustomerNotes] = useState(claim.customerNotes ?? "");
  const [passengersQuestion, setPassengersQuestion] = useState(claim.passengersQuestion ?? "");
  const [adminNoteData, setAdminNoteData] = useState<OutputData | undefined>(
    normalizeAdminNotes(claim.adminNotes),
  );

  const reservation = claim.legalSpecification.impactedSegment.reservation;

  function onSaveCustomerNote() {
    saveCustomerNote.mutate({ customerNotes, passengersQuestion });
  }

  function onSaveAdminNote() {
    if (adminNoteData) {
      saveAdminNote.mutate(adminNoteData);
    }
  }

  return (
    <div className="bg-white px-2 py-3">
      <div className="mb-4 overflow-hidden rounded border bg-white text-left shadow-md sm:rounded-lg">
        <div className="py-5 sm:p-0">
          <div className="bg-gray-50 p-6 shadow">
            <div className="flex items-center justify-between">
              <h3 className="inline-flex text-base leading-7 font-semibold text-gray-900">
                Résumé du dossier
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <button
                        type="button"
                        onClick={() => copyToClipboard(claim.claimNumber.toString())}
                        className="ml-2 inline-flex cursor-pointer items-center rounded-md bg-gray-50 px-2 py-0.5 text-xs font-medium text-slate-800 ring-1 ring-gray-500/20 ring-inset"
                      />
                    }
                  >
                    № #{claim.claimNumber}
                  </TooltipTrigger>
                  <TooltipContent>Notre référence</TooltipContent>
                </Tooltip>
                <button
                  type="button"
                  onClick={() => copyToClipboard(reservation.gdsPnrId ?? "")}
                  className="ml-2 inline-flex cursor-pointer items-center rounded-md bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-gray-500/20 ring-inset"
                >
                  PNR: {reservation.gdsPnrId}
                </button>
              </h3>
              {claim.assignedAdmin?.profilePictureUrl ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <img
                        src={claim.assignedAdmin.profilePictureUrl}
                        alt={claim.assignedAdmin.fullName}
                        className="h-8 w-8 flex-shrink-0 rounded-full object-cover ring-2 ring-white"
                      />
                    }
                  />
                  <TooltipContent>{claim.assignedAdmin.fullName}</TooltipContent>
                </Tooltip>
              ) : claim.assignedAdmin ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200" />
                    }
                  >
                    <span className="text-xs font-medium text-gray-600">
                      {claim.assignedAdmin.fullName?.charAt(0)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{claim.assignedAdmin.fullName}</TooltipContent>
                </Tooltip>
              ) : null}
            </div>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
              Aperçu de la demande, des pièces et des personnes impactées.
            </p>
          </div>

          <div className="px-4">
            <dl className="sm:divide-y sm:divide-gray-200">
              <div className="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm leading-6 font-medium text-gray-700">
                  <span className="inline-flex">
                    <MapIcon className="mr-2 h-5 w-5 text-slate-400" />
                    Agence
                  </span>
                </dt>
                <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                  {claim.tenantAssociation.sourceTenant.tenantBranding?.logoUrl ? (
                    <img
                      className="max-w-40 rounded"
                      src={claim.tenantAssociation.sourceTenant.tenantBranding.logoUrl}
                      alt={claim.tenantAssociation.sourceTenant.name}
                    />
                  ) : null}
                </dd>
              </div>

              <div className="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm leading-6 font-medium text-gray-700">
                  <span className="inline-flex">
                    <ScaleIcon className="mr-2 h-5 w-5 text-slate-400" />
                    Adversaire
                  </span>
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                  <SetClaimAdversary claimId={claim.id} currentAdversary={claim.adversaryCompany} />
                </dd>
              </div>

              <ClaimRequests claim={claim} />

              <div className="bg-white py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                <dt className="text-sm leading-6 font-medium text-gray-700">
                  <span className="inline-flex">
                    <UserIcon className="mr-2 h-5 w-5 text-slate-400" />
                    Agent
                  </span>
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                  <SetTravelAgencyAgent
                    claimId={claim.id}
                    currentAdmin={reservation.travelAgencyAgent?.admin}
                  />
                </dd>
              </div>

              {isLeisureOffice(reservation.travelAgencyOffice) ? null : (
                <div className="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm leading-6 font-medium text-gray-700">
                    <span className="inline-flex">
                      <BuildingOffice2Icon className="mr-2 h-5 w-5 text-slate-400" />
                      Entreprise
                    </span>
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    <SetBusinessTravelCompany
                      claimId={claim.id}
                      currentTravelAgencyCompany={reservation.travelAgencyCompany}
                    />
                  </dd>
                </div>
              )}

              <ClaimReasonCxDisclosure claimId={claim.id} />

              {can([...UPCLAIM_ROLES, "LAWYER"]) ? (
                <div className="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="inline-flex text-sm leading-6 font-medium text-gray-700">
                    <span className="inline-flex">
                      <LockClosedIcon className="mr-2 h-5 w-5 text-slate-400" />
                      Notes internes
                    </span>
                  </dt>
                  <dd className="mt-1 flex flex-col items-end text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    <TextEditor
                      className="mb-5"
                      initialData={normalizeAdminNotes(claim.adminNotes)}
                      canEdit={can([...UPCLAIM_ROLES, "LAWYER"])}
                      onUpdateNote={setAdminNoteData}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onSaveAdminNote}
                      disabled={saveAdminNote.isPending}
                    >
                      Sauvegarder
                    </Button>
                  </dd>
                </div>
              ) : null}

              <div className="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm leading-6 font-medium text-gray-700">
                  <span className="inline-flex">
                    <ChatBubbleOvalLeftEllipsisIcon className="mr-2 h-5 w-5 flex-shrink-0 text-slate-400" />
                    Notes agents
                  </span>
                </dt>
                <dd className="mt-1 flex flex-col items-end text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                  <Textarea
                    rows={4}
                    name="agent-notes"
                    id="agent-notes"
                    className="mb-5 block w-full"
                    value={customerNotes}
                    onChange={(event) => setCustomerNotes(event.target.value)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onSaveCustomerNote}
                    disabled={saveCustomerNote.isPending}
                  >
                    Sauvegarder
                  </Button>
                </dd>
              </div>

              <div className="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm leading-6 font-medium text-gray-700">
                  <span className="inline-flex">
                    <QuestionMarkCircleIcon className="mr-2 h-5 w-5 flex-shrink-0 text-slate-400" />
                    Question Passagers
                  </span>
                </dt>
                <dd className="mt-1 flex flex-col items-end text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                  <Textarea
                    rows={4}
                    name="passengers-question"
                    id="passengers-question"
                    placeholder="Question à poser aux passagers"
                    className="mb-5 block w-full"
                    value={passengersQuestion}
                    onChange={(event) => setPassengersQuestion(event.target.value)}
                  />
                  <Can permissions={[...UPCLAIM_ROLES, "CUSTOMER_ADMIN"]}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onSaveCustomerNote}
                      disabled={saveCustomerNote.isPending}
                    >
                      Sauvegarder
                    </Button>
                  </Can>
                </dd>
              </div>

              <div className="py-6 sm:px-6">
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-sm leading-6 font-medium text-gray-700">
                    <span className="inline-flex">
                      <FolderOpenIcon className="mr-2 h-5 w-5 text-slate-400" />
                      Documents
                    </span>
                  </dt>
                  {/* « See all », iOS-style: on the section's title, above what it widens to every file. */}
                  {can([...UPCLAIM_ROLES, "LAWYER"]) ? (
                    <Link
                      to="/claims/$claimId/files"
                      params={{ claimId: claim.id }}
                      // Opened from the claims list, the list underneath keeps its state.
                      search={(prev) => prev}
                      className="link inline-flex items-center gap-0.5 text-sm font-medium"
                      onClick={(event) => {
                        if (!isBoxFolderAvailable(claim.boxFolderId)) {
                          event.preventDefault();
                        }
                      }}
                    >
                      Tous les fichiers
                      <ChevronRight className="size-4" />
                    </Link>
                  ) : null}
                </div>
                {can([...UPCLAIM_ROLES, "LAWYER"]) ? (
                  <Tabs defaultValue="claim-document">
                    <TabsList className="mt-2 grid w-full grid-cols-2">
                      <TabsTrigger value="claim-document">
                        Amiable ({claim.claimDocuments.length})
                      </TabsTrigger>
                      <TabsTrigger value="procedure">
                        Procédure ({claim.procedure?.procedureDocuments?.length || 0})
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent value="claim-document">
                      <ClaimDocuments claim={claim} />
                    </TabsContent>
                    <TabsContent value="procedure">
                      <ProcedureDocuments claim={claim} />
                    </TabsContent>
                  </Tabs>
                ) : (
                  <ClaimDocuments claim={claim} />
                )}
              </div>

              <div className="-mx-4 py-6">
                <ExpensesList
                  claim={claim}
                  claimExpenses={claim.claimExpenses}
                  showActions
                  header={
                    <div className="px-3 text-left">
                      <dt className="text-sm leading-6 font-medium text-gray-700">
                        <span className="inline-flex">
                          <ReceiptRefundIcon className="mr-2 h-5 w-5 text-slate-400" />
                          Toutes les factures
                        </span>
                      </dt>
                    </div>
                  }
                />
              </div>

              <Can permissions={["LAWYER", ...UPCLAIM_ROLES]}>
                <div className="-mx-4 py-6">
                  <MonetaryDemandsList
                    claim={claim}
                    monetaryDemands={claim.claimMonetaryDemands}
                    claimExpenses={claim.claimExpenses}
                    header={
                      <div className="px-3 text-left">
                        <dt className="text-sm leading-6 font-medium text-gray-700">
                          <span className="inline-flex">
                            <ScaleIcon className="mr-2 h-5 w-5 text-slate-400" />
                            Demandes monétaires
                          </span>
                          <p className="my-1 text-xs text-gray-500">
                            Toutes les demandes envers notre adversaire centralisées ici.
                          </p>
                        </dt>
                      </div>
                    }
                  />
                </div>
              </Can>

              {showTravelerPayments ? (
                <TravelerPayments
                  claimId={claim.id}
                  claimNumber={claim.claimNumber}
                  claimExpenses={claim.claimExpenses}
                />
              ) : null}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
